<template>
  <div class="tree-node" :class="{ 'node-expanded': expanded }">
    <div class="node-content" @click="toggleExpanded" :class="{ 'node-selected': isSelected }">
      <div class="node-indent" :style="{ width: `${level * 12}px` }"></div>
      <div v-if="hasChildren" class="node-toggle">
        <feather-icon :icon="expanded ? 'ChevronDownIcon' : 'ChevronRightIcon'" size="14" />
      </div>
      <div v-else class="node-toggle-placeholder"></div>

      <div class="node-icon">
        <feather-icon
          :icon="getNodeIcon()"
          size="14"
          :class="{
            'node-folder-icon': item.type === 'directory' || item.type === 'drive',
            'folder-open': isSelected && (item.type === 'directory' || item.type === 'drive')
          }"
        />
      </div>

      <div class="node-label" @click.stop="nodeClick">
        {{ item.name }}
        <span v-if="showItemCounts" class="item-count">
          ({{ item.folder_count || 0 }}/{{ item.file_count || 0 }})
        </span>
      </div>
    </div>

    <div v-if="expanded && hasChildren" class="node-children">
      <div v-if="childrenLoading" class="tree-loading-children">
        <div class="spinner-border spinner-border-sm text-primary" role="status">
          <span class="sr-only">Carregando...</span>
        </div>
      </div>
      <ul v-else class="child-list">
        <li v-for="(child, index) in childrenItems" :key="`child-${index}`" class="child-item">
          <tree-node
            :item="child"
            :level="level + 1"
            :is-shared-context="isSharedContext"
            :current-path="currentPath"
            :file-explorer-service="fileExplorerService"
            :drive-public-service="drivePublicService"
            @node-click="handleChildNodeClick"
          />
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import '@/assets/scss/FileExplorer.scss';

export default {
  name: 'TreeNode',
  props: {
    item: {
      type: Object,
      required: true
    },
    level: {
      type: Number,
      default: 0
    },
    currentPath: {
      type: String,
      default: ''
    },
    isSharedContext: {
      type: Boolean,
      default: false
    },
    fileExplorerService: {
      type: Object,
      required: true
    },
    drivePublicService: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      expanded: false,
      childrenItems: [],
      childrenLoading: false,
      childrenLoaded: false,
      lastUpdateTime: Date.now() // Timestamp da última atualização
    };
  },
  computed: {
    hasChildren() {
      // Se já carregamos os filhos, verificamos os childrenItems
      if (this.childrenLoaded) {
        return this.childrenItems.length > 0;
      }

      // Verificar se temos a informação total_folders ou has_children do backend
      if (this.item.has_children !== undefined) {
        return this.item.has_children;
      }

      // Verificar se temos folder_count
      if (this.item.folder_count !== undefined) {
        return this.item.folder_count > 0;
      }

      // Se temos contagem de pastas ou arquivos
      if (this.item.folder_count !== undefined || this.item.file_count !== undefined) {
        // Para a TreeView, só consideramos folders como children, pois arquivos não são exibidos
        return this.item.folder_count > 0;
      }

      // Fallback: Se não temos nenhuma informação de children, assumimos que não há
      return false;
    },
    isSelected() {
      // Check if this node represents the current path
      // Only select a node if it matches the exact path
      if (this.item.path === this.currentPath) {
        return true;
      }
      // For root/drive with empty path, only select if it's explicitly the root item
      // and we're at the root path (prevents selecting all drives automatically)
      if (this.currentPath === '' && this.item.type === 'drive' && this.item.path === '') {
        return true;
      }
      return false;
    },
    // Mostrar contagem de itens (pastas e arquivos) se as propriedades estiverem disponíveis
    showItemCounts() {
      return this.item.folder_count !== undefined || this.item.file_count !== undefined;
    },
    isDrivePublic() {
      return this.drivePublicService?.isPublic ?? false;
    }
  },
  watch: {
    // Auto-expand parent folders of the selected item
    currentPath: {
      immediate: true,
      handler(newPath) {
        if (newPath && this.hasChildren && this.shouldExpandForPath(newPath)) {
          this.expanded = true;
          this.loadChildrenIfNeeded();
        }
      }
    },
    // Observar mudanças no item para detectar se precisamos recarregar
    item: {
      deep: true,
      handler(newItem, oldItem) {
        // Se o resource_id é o mesmo, mas a estrutura mudou, pode ser que
        // precisemos recarregar os filhos
        if (newItem.resource_id === oldItem.resource_id &&
            (newItem.folder_count !== oldItem.folder_count ||
             newItem.children !== oldItem.children)) {
          console.log('Detectada mudança no item:', newItem.name, 'forçando recarga de filhos');

          // Só recarregar se já estiver expandido
          if (this.expanded) {
            // Forçar recarga dos filhos
            this.childrenLoaded = false;
            this.loadChildrenIfNeeded();
          }
        }
      }
    }
  },
  mounted() {
    // Ouvir eventos de atualização da estrutura da árvore
    this.$root.$on('tree-structure-updated', this.handleTreeStructureUpdate);
  },
  beforeDestroy() {
    // Limpar event listener quando o componente for destruído
    this.$root.$off('tree-structure-updated', this.handleTreeStructureUpdate);
  },
  methods: {
    // Manipular evento de atualização da árvore
    handleTreeStructureUpdate(data) {
      // Verificar se este nó ou algum de seus filhos pode ser afetado
      if (!data || !data.updatedNodeId) return;

      // Operação realizada (create, delete, update, rename)
      const action = data.action || 'unknown';
      // Tipo do item (file, directory)
      const itemType = data.itemType || 'unknown';

      console.log(`TreeNode ${this.item.name} recebeu evento: ${action} - ${itemType}`, data);

      // CASO 1: Este é o nó que foi diretamente afetado (seu parent_id foi passado no evento)
      if (this.item.resource_id === data.updatedNodeId) {
        console.log(`Nó ${this.item.name} diretamente afetado pela operação ${action}`);

        // Atualizar contadores diretamente com base na operação
        if (this.item.folder_count !== undefined && itemType === 'directory') {
          // Incrementar ou decrementar contador de pastas
          if (action === 'create') {
            this.item.folder_count++;
            this.item.has_children = true; // Se adicionou uma pasta, agora tem filhos
            console.log(`Incrementando contador de pastas para ${this.item.name}: ${this.item.folder_count}`);
          } else if (action === 'delete') {
            this.item.folder_count = Math.max(0, this.item.folder_count - 1);
            this.item.has_children = this.item.folder_count > 0; // Verificar se ainda tem pastas
            console.log(`Decrementando contador de pastas para ${this.item.name}: ${this.item.folder_count}`);
          }
          // Para renomeação, o contador não muda
        }

        if (this.item.file_count !== undefined && itemType === 'file') {
          // Incrementar ou decrementar contador de arquivos
          if (action === 'create') {
            this.item.file_count++;
            console.log(`Incrementando contador de arquivos para ${this.item.name}: ${this.item.file_count}`);
          } else if (action === 'delete') {
            this.item.file_count = Math.max(0, this.item.file_count - 1);
            console.log(`Decrementando contador de arquivos para ${this.item.name}: ${this.item.file_count}`);
          }
          // Para renomeação, o contador não muda
        }

        // Se estiver expandido, recarregar os filhos para atualizar o conteúdo
        // Necessário para todas as operações, incluindo renomeação
        if (this.expanded) {
          this.childrenLoaded = false;
          this.loadChildrenIfNeeded(true);
        }
      }
      // CASO 2: Este nó contém um filho que foi afetado
      else if (this.childrenItems && this.childrenItems.some(child =>
        child.resource_id === data.updatedNodeId ||
        child.resource_id === data.itemId)) {

        console.log(`Nó ${this.item.name} contém um filho afetado pela operação ${action}`);

        // Para renomeação, podemos atualizar o nome do filho diretamente para evitar recarregar
        if (action === 'rename' && data.itemId && data.newName) {
          // Encontrar o filho a ser renomeado
          const childToRename = this.childrenItems.find(child => child.resource_id === data.itemId);

          if (childToRename) {
            console.log(`Renomeando filho de ${childToRename.name} para ${data.newName}`);
            // Atualizar o nome e possivelmente o caminho
            this.$set(childToRename, 'name', data.newName);

            // Se o caminho foi fornecido, atualize-o também
            if (data.newPath) {
              this.$set(childToRename, 'path', data.newPath);
            }

            // Não é necessário recarregar todos os filhos neste caso
            return;
          }
        }

        // Para outras operações, recarregar os filhos para mostrar as mudanças
        if (this.expanded) {
          this.childrenLoaded = false;
          this.loadChildrenIfNeeded(true);
        }
      }
    },

    // Manipular evento para atualização global de contadores na árvore
    handleCountersRefresh(data) {
      // Esse evento é disparado quando precisamos de uma atualização mais ampla
      // Por exemplo, quando uma pasta com subpastas é excluída
      console.log(`TreeNode ${this.item.name} recebeu evento de atualização global`, data);

      const shouldUpdate =
        // Sempre atualizar raiz e nós expandidos
        this.expanded ||
        // Atualizar nós específicos mencionados no evento
        (data.parentId && this.item.resource_id === data.parentId) ||
        this.item.resource_id === data.targetNodeId;

      if (shouldUpdate) {
        console.log(`Atualizando nó ${this.item.name} com atualização global`);
        this.childrenLoaded = false;
        this.loadChildrenIfNeeded(true);
      }
    },

    async toggleExpanded() {
      if (this.hasChildren) {
        this.expanded = !this.expanded;

        // Carregar filhos quando expandir, se ainda não foram carregados
        if (this.expanded) {
          this.loadChildrenIfNeeded(true); // Forçar recarga quando abrir
        }
      }
    },

    async loadChildrenIfNeeded(forceReload = false) {
      // Verificar se precisamos carregar ou recarregar os filhos
      if ((!this.childrenLoaded || forceReload) && !this.childrenLoading) {
        // Verifica se folder_count é zero, nesse caso não precisa fazer a requisição
        // (exceto se estiver forçando a recarga)
        if (this.item.folder_count === 0 && !forceReload) {
          this.childrenItems = [];
          this.childrenLoaded = true;
          return;
        }

        console.log(`Carregando filhos para ${this.item.name} [ID: ${this.item.resource_id}]`);
        this.childrenLoading = true;

        try {
          let children = [];
          if(this.isDrivePublic){
            const response = await this.drivePublicService.getResources(
              this.item.path,
              this.item.resource_id,
              this.item.parent_id,
              true
            );
            children = response.items;
          }else{
            // Buscar filhos atualizado do servidor
            children = await this.fileExplorerService.getDirectoryChildren(
              this.item.path,
              this.item.resource_id,
              this.isSharedContext
            );
          }

          // Filtrar apenas diretórios para a TreeView
          this.childrenItems = children.filter(child =>
            child.type === 'directory' || child.type === 'drive'
          );

          // Atualizar contadores baseado na resposta completa do servidor
          const folderCount = children.filter(child => child.type === 'directory').length;
          const fileCount = children.filter(child => child.type === 'file').length;

          console.log(`Atualizando contadores para ${this.item.name}:
            pastas=${folderCount}, arquivos=${fileCount}`);

          // Atualizar contadores com os valores atuais do servidor
          this.item.folder_count = folderCount;
          this.item.file_count = fileCount;
          this.item.has_children = folderCount > 0;

          // Marcar dados como carregados
          this.childrenLoaded = true;
          this.lastUpdateTime = Date.now();
        } catch (error) {
          console.error(`Erro ao carregar filhos para ${this.item.name}:`, error);
        } finally {
          this.childrenLoading = false;
        }
      }
    },

    // Método público para recarregar explicitamente os filhos deste nó
    reloadChildren() {
      console.log('Solicitação explícita para recarregar filhos do nó:', this.item.name);
      this.childrenLoaded = false;

      // Se o nó estiver expandido, carregar filhos imediatamente
      if (this.expanded) {
        this.loadChildrenIfNeeded(true);
      }
    },

    nodeClick() {
      // Emitir evento apenas para diretórios
      if (this.item.type === 'directory' || this.item.type === 'drive') {
        this.$emit('node-click', this.item);
      }
    },
    handleChildNodeClick(item) {
      // Propagar o evento do filho para o pai
      this.$emit('node-click', item);
    },
    getNodeIcon() {
      if (this.item.type === 'drive') {
        return 'HardDriveIcon';
      } else if (this.item.type === 'directory') {
        // Instead of using a different icon, we'll use the same icon but with a class
        return 'FolderIcon';
      }
    },
    shouldExpandForPath(path) {
      // Check if the current path is a child of this node
      // This helps auto-expand parent directories when a child is selected
      if (!path || !this.item.path) return false;

      // If this is a parent of the selected path, expand it
      return path.startsWith(this.item.path + '/');
    }
  }
}
</script>

<style lang="scss">
/* Estilos movidos para o arquivo @/assets/scss/FileExplorer.scss */
</style>
